import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'

import {
  Autocomplete as LibAutocomplete,
  Textarea,
  Textfield,
  Select,
  Icon,
  Modal,
  ModalContent,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  Stack,
  Typography,
  MenuItem,
  Link,
  useMediaQuery,
  TextField
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import InputController from 'components/InputController'
import UploadComponent from 'components/UploadComponent'
import { FileParams } from 'components/UploadComponent/FileList'

import {
  CustomFieldDateType,
  displayDateToISO,
  isoDateToDisplay,
  isoDateToLocalDate,
  localDateToISO,
  maskDateInput,
  normalizeDateType
} from 'utils/custom-field-date'
import {
  buildUploadAccept,
  extractUploadDocuments,
  resolveUploadSettings,
  toUploadFormValue,
  uploadHelperText,
  uploadInvalidTypeMessage,
  UploadFileType,
  UploadSettings
} from 'utils/customFieldUpload'
import { openDocumentViewer } from 'utils/documentViewer'

import { theme } from 'styles/theme'

export interface CustomFieldOption {
  label: string
  value: string
}

export interface FieldConfiguration {
  type: string
  title: string
  value: string
  accept_text?: string
  description: string
  options?: Array<{
    uuid: string
    label: string
    value: string
  }>
  display_mode?: 'list' | 'dropdown'
  accepted_types?: UploadFileType[]
  multiple?: boolean
  max_files?: number
}

export interface DocumentFile {
  document_fileable_type: string
  uuid: string
  doc_type: string
  status: string
  description: string
  created_at: string
  updated_at: string
  doc_file_url: string
  doc_file_name: string
  doc_file_size: number
}

export interface CustomField {
  uuid: string
  field_type: string
  field_configuration: FieldConfiguration
  document_file?: DocumentFile
  upload_settings?: UploadSettings
}
export interface CustomFieldItem {
  uuid: string
  name: string
  required: boolean
  field_type: string
  options?: CustomFieldOption[]
  custom_field?: CustomField
  description?: string
  value?: any
  field_value?: any
  field_configuration?: any
  title?: string
  context_type?: string
  display_mode?: 'list' | 'dropdown'
  upload_settings?: UploadSettings
}

interface CustomFieldRendererProps {
  field: CustomFieldItem
  control: Control<any>
  fieldNamePrefix?: string
  descriptionText?: string
  setValue?: UseFormSetValue<any>
  showViewIcon?: boolean // Flag opcional para mostrar ícone de visualização
  setUploadIsDirty?: React.Dispatch<React.SetStateAction<boolean>>
  setUploadIsLoading?: React.Dispatch<React.SetStateAction<boolean>>
  isLoading?: boolean
}

// Mesmo input do portal (SC-11044): o texto digitado tem máscara DD/MM/AAAA e só
// vira ISO quando a data está completa; o calendário só grava no onAccept. Antes o
// DatePicker reinterpretava cada tecla como data ("22/02/2" virava 02/02/2022) e
// relia o ISO salvo em UTC, mostrando o dia anterior.
const DateFieldInput = ({
  controllerField,
  fieldState,
  views,
  inputFormat,
  placeholder,
  dateType
}: {
  controllerField: any
  fieldState: any
  views: any
  inputFormat: string
  placeholder: string
  dateType: CustomFieldDateType
}) => {
  const [inputText, setInputText] = useState(() =>
    isoDateToDisplay(controllerField.value, dateType)
  )
  // Último valor que o próprio input mandou pro form. Quando o valor do form
  // muda por fora (ex.: preenchimento inicial), o texto é refeito a partir dele.
  const lastEmittedRef = useRef(controllerField.value)

  useEffect(() => {
    if (controllerField.value === lastEmittedRef.current) return
    lastEmittedRef.current = controllerField.value
    setInputText(isoDateToDisplay(controllerField.value, dateType))
  }, [controllerField.value, dateType])

  const emit = (value: string) => {
    lastEmittedRef.current = value
    controllerField.onChange(value)
  }

  return (
    <DatePicker
      views={views as any}
      inputFormat={inputFormat}
      disableMaskedInput
      // No modo mobile o MUI deixa o input readOnly e só abre o calendário.
      desktopModeMediaQuery="@media all"
      value={isoDateToLocalDate(controllerField.value, dateType)}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onChange={() => {}}
      onAccept={(date: Date | null) => {
        const iso = localDateToISO(date, dateType)
        emit(iso)
        setInputText(isoDateToDisplay(iso, dateType))
        controllerField.onBlur()
      }}
      PopperProps={{
        placement: 'bottom-end'
      }}
      renderInput={(params: any) => (
        <Textfield
          {...params}
          fullWidth
          placeholder={placeholder}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          inputProps={{
            ...params.inputProps,
            placeholder,
            inputMode: 'numeric',
            value: inputText
          }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const masked = maskDateInput(e.target.value, dateType)
            setInputText(masked)
            // Data incompleta ou inexistente vai crua pro form, pra validação
            // acusar "Data inválida" em vez de "Campo obrigatório".
            emit(displayDateToISO(masked, dateType) ?? masked)
          }}
          onBlur={() => controllerField.onBlur()}
        />
      )}
    />
  )
}

const CustomFieldRenderer: React.FC<CustomFieldRendererProps> = ({
  field,
  control,
  fieldNamePrefix = 'extraField',
  descriptionText,
  setValue,
  showViewIcon = false,
  setUploadIsDirty,
  setUploadIsLoading,
  isLoading
}) => {
  const fieldName = `${fieldNamePrefix}_${field.uuid}`
  const uploadRef = useRef<UploadComponent | null>(null)
  const [files, setFiles] = useState<FileParams[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const isMobile = useMediaQuery('(max-width: 960px)')

  // SC-11348: tipos aceitos, vários arquivos e limites do campo de upload
  const isUploadField = field.field_type === 'upload'
  const uploadSettings = useMemo(() => resolveUploadSettings(field), [field])
  const [initialUploadFiles, setInitialUploadFiles] = useState<FileParams[]>([])
  // Só sincroniza o valor do form a partir da lista depois que o usuário mexe
  // nos arquivos; na montagem quem define o valor é o preset abaixo (ou a tela).
  const uploadFilesChangedRef = useRef(false)

  // Documentos já gravados: o encoder do customer manda em `field_value`
  // (objeto único ou array). Item sem nome nem URL é arquivo que não existe
  // mais, e fica fora da lista.
  const initialDocuments = useMemo(() => {
    if (!isUploadField) return []

    const fromFieldValue = extractUploadDocuments(field.field_value)
    const documents = fromFieldValue.length
      ? fromFieldValue
      : extractUploadDocuments(field.value)

    return documents.filter((doc) => doc.doc_file_name || doc.doc_file_url)
  }, [isUploadField, field.field_value, field.value])

  const initialDocumentsKey = initialDocuments.map((doc) => doc.uuid).join(',')

  // Preset do UploadComponent com os documentos existentes
  useEffect(() => {
    if (!isUploadField || !initialDocuments.length) return

    const presetData: FileParams[] = initialDocuments.map((doc) => ({
      id: doc.uuid,
      name: doc.doc_file_name || '',
      readableSize: doc.doc_file_size || 0,
      preview: doc.doc_file_url || '',
      uploaded: true,
      url: doc.doc_file_url || '',
      error: false,
      progress: 100,
      file: new File([], doc.doc_file_name || '')
    }))

    setFiles(presetData)
    // Entra no estado interno do UploadComponent: é o que permite excluir o
    // arquivo no servidor pelo X da lista.
    setInitialUploadFiles(presetData)

    if (setValue) {
      setValue(
        fieldName,
        toUploadFormValue(
          presetData.map((file) => file.id),
          uploadSettings.multiple
        )
      )
    }
  }, [isUploadField, initialDocumentsKey, fieldName])

  const handleUploadFilesChange: React.Dispatch<
    React.SetStateAction<FileParams[]>
  > = (action) => {
    uploadFilesChangedRef.current = true
    setFiles(action)
  }

  // Arquivo enviado ou removido: o valor do campo passa a ser o uuid (1
  // arquivo) ou a lista de uuids (vários arquivos) do que está na lista.
  useEffect(() => {
    if (!isUploadField || !setValue || !uploadFilesChangedRef.current) return

    const uploadedIds = files
      .filter((file) => file.uploaded && file.id && !file.error)
      .map((file) => String(file.id))

    setValue(fieldName, toUploadFormValue(uploadedIds, uploadSettings.multiple))
  }, [files, fieldName, setValue, uploadSettings.multiple])

  useEffect(() => {
    if (files.length > 0) {
      const fileWithError = files.find((file) => file.error)
      if (fileWithError) {
        setFiles((prevFiles) => prevFiles.filter((file) => !file.error))
      }
    }
  }, [files])

  const renderUploadField = () => {
    const activeFiles = files.filter((file) => !file.error)
    const remainingFiles = uploadSettings.max_files - activeFiles.length
    const limitReached = remainingFiles <= 0
    const uploadingFiles = activeFiles.some((file) => !file.uploaded)

    const disabledText = uploadSettings.multiple
      ? `Limite de ${uploadSettings.max_files} arquivos atingido. Remova um para anexar outro.`
      : 'Remova o documento atual para anexar outro'

    return (
      <Box mb="-8px">
        <Stack gap="2px" mb={0.5}>
          <Typography variant="smLight" color={theme.palette.neutral[200]}>
            {field.title || field.name}
            {field.required ? '*' : ''}
          </Typography>
          {field.description && (
            <Typography variant="smLight" color={theme.palette.neutral[400]}>
              {field.description}
            </Typography>
          )}
        </Stack>
        <Controller
          control={control}
          name={fieldName}
          render={({ fieldState }) => (
            <Box>
              <UploadComponent
                setUploadIsDirty={setUploadIsDirty}
                type="custom_fields"
                // O maxFiles do dropzone vale por soltura; o total é controlado
                // pelo restante e pelo disabled.
                maxFiles={Math.max(remainingFiles, 1)}
                maxSize={uploadSettings.max_file_size}
                accept={buildUploadAccept(uploadSettings.accepted_types)}
                invalidTypeMessage={uploadInvalidTypeMessage(uploadSettings)}
                tooManyFilesMessage={
                  uploadSettings.multiple
                    ? `Este campo aceita no máximo ${uploadSettings.max_files} arquivos. Você ainda pode anexar ${remainingFiles}.`
                    : 'Este campo aceita apenas 1 arquivo.'
                }
                extraFormData={{ custom_field_uuid: field.uuid }}
                setLoadingExternal={setUploadIsLoading}
                title={field.title || field.name}
                disabledText={disabledText}
                subTitle={
                  field.description ||
                  (uploadSettings.multiple
                    ? 'Clique aqui ou arraste os arquivos para anexá-los'
                    : 'Clique aqui ou arraste o arquivo para anexá-lo')
                }
                isLoading={isLoading}
                docType="terms"
                ref={uploadRef}
                disableList={true}
                initialFiles={initialUploadFiles}
                setStateExternal={handleUploadFilesChange}
                disabled={limitReached}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                gap={1}
                mt="-4px"
                mb="8px"
                ml="5px"
              >
                <Typography
                  variant="xsLight"
                  color={theme.palette.neutral[400]}
                >
                  {uploadHelperText(uploadSettings)}
                </Typography>
                {uploadSettings.multiple && (
                  <Typography
                    variant="xsLight"
                    color={theme.palette.neutral[400]}
                    whiteSpace="nowrap"
                  >
                    {`${activeFiles.length} de ${uploadSettings.max_files} arquivos`}
                  </Typography>
                )}
              </Stack>

              {activeFiles.length > 0 && (!isLoading || uploadingFiles) && (
                <Stack gap="4px" mb="8px">
                  {activeFiles.map((file) => (
                    <Box
                      key={file.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Typography
                        ml="5px"
                        color={theme.palette.neutral[500]}
                        variant="xsLight"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {file.name}
                        {!file.uploaded ? ' (enviando...)' : ''}
                      </Typography>
                      <Box display="flex" alignItems="center" gap="8px">
                        {showViewIcon && file.uploaded && file.url && (
                          <Icon
                            sx={{
                              cursor: 'pointer',
                              width: '18px',
                              height: '18px'
                            }}
                            onClick={() =>
                              openDocumentViewer({
                                url: file.url,
                                filename: file.name || '',
                                fallbackToDirectOpen: true
                              })
                            }
                            icon="visibility"
                            color={'disabled'}
                          />
                        )}
                        <Icon
                          sx={{
                            cursor: 'pointer',
                            width: '18px',
                            height: '18px'
                          }}
                          onClick={() =>
                            uploadRef.current?.handleDelete(file.id)
                          }
                          icon="cancel"
                          color={'disabled'}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          )}
        />
      </Box>
    )
  }

  const renderFieldByType = () => {
    switch (field.field_type) {
      case 'short_text':
        return (
          <InputController control={control} name={fieldName}>
            <Textfield
              label={
                <Stack>
                  <Typography
                    variant="smLight"
                    color={theme.palette.neutral[200]}
                    sx={{ mb: 0.2 }}
                  >
                    {field.name}
                    {field.required ? '*' : ''}
                  </Typography>
                  {field.description ? (
                    <Typography
                      variant="smLight"
                      sx={{ mb: 0.5 }}
                      color={theme.palette.neutral[400]}
                    >
                      {field.description}
                    </Typography>
                  ) : null}
                </Stack>
              }
              placeholder="Preencha este campo"
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  color: theme.palette.neutral[400],
                  opacity: 1
                }
              }}
            />
          </InputController>
        )

      case 'long_text':
        return (
          <Box>
            <Stack>
              <Typography sx={{ mb: 0.2 }}>
                {field.name}
                {field.required ? '*' : ''}
              </Typography>
              {field.description ? (
                <Typography sx={{ mb: 0.5 }} color="neutral.400">
                  {field.description}
                </Typography>
              ) : null}
            </Stack>
            <Controller
              name={fieldName}
              control={control}
              render={({ field: controllerField, fieldState }) => (
                <TextField
                  {...controllerField}
                  placeholder="Preencha este campo"
                  multiline
                  rows={3}
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '14px',
                      padding: '8px'
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '14px'
                    },
                    '& .MuiFormHelperText-root': {
                      fontSize: '14px'
                    }
                  }}
                  inputProps={{
                    style: { fontSize: '14px' }
                  }}
                />
              )}
            />
          </Box>
        )

      case 'checkbox': {
        // Obter opções da nova estrutura field_configuration.options ou fallback para field.options
        // Priorizar field.options (que vem do useCustomFields mapeado) sobre field.custom_field
        const checkboxOptions =
          field.options ||
          field.custom_field?.field_configuration?.options ||
          field.field_configuration?.options ||
          []

        const displayMode =
          field.display_mode ||
          field.custom_field?.field_configuration?.display_mode ||
          field.field_configuration?.display_mode ||
          'list'

        return (
          <Box>
            <Stack>
              <Typography
                variant="smLight"
                color={theme.palette.neutral[200]}
                sx={{ mb: 0.5 }}
              >
                {field.custom_field?.field_configuration?.title || field.name}
                {field.required ? '*' : ''}
              </Typography>
              {(field.custom_field?.field_configuration?.description ||
                field.description) && (
                <Typography
                  variant="smLight"
                  mb={0.5}
                  color={theme.palette.neutral[400]}
                >
                  {field.custom_field?.field_configuration?.description ||
                    field.description}
                </Typography>
              )}
            </Stack>
            <Controller
              control={control}
              name={fieldName}
              render={({ field: controllerField, fieldState }) =>
                displayMode === 'dropdown' ? (
                  <LibAutocomplete
                    multiple
                    options={checkboxOptions.map((opt: any) => ({
                      label: opt.label,
                      value: opt.value
                    }))}
                    value={
                      Array.isArray(controllerField.value)
                        ? controllerField.value.map((item: any) =>
                            typeof item === 'object'
                              ? { label: item.label, value: item.value }
                              : {
                                  label:
                                    checkboxOptions.find(
                                      (o: any) => o.value === item
                                    )?.label || item,
                                  value: item
                                }
                          )
                        : []
                    }
                    onChange={(_: any, newValue: any) => {
                      controllerField.onChange(
                        (Array.isArray(newValue) ? newValue : []).map(
                          (opt: any) => ({
                            label: opt.label,
                            value: opt.value
                          })
                        )
                      )
                    }}
                    getOptionLabel={(option: any) => option?.label || ''}
                    isOptionEqualToValue={(option: any, val: any) =>
                      option.value === val.value
                    }
                    renderInput={(params: any) => (
                      <Textfield
                        {...params}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        placeholder="Selecione as opções..."
                      />
                    )}
                  />
                ) : (
                  <Box sx={{ mb: -1 }}>
                    {checkboxOptions.map((option: any, optIndex: number) => (
                      <Stack
                        key={option.uuid || option.value || optIndex}
                        width="100%"
                        borderRadius="8px"
                        border={`1px solid ${theme.palette.neutral[600]}`}
                        sx={{ mb: 1 }}
                      >
                        <Stack direction="row" alignItems="center" gap="8px">
                          <Typography
                            color={theme.palette.neutral[200]}
                            variant="smLight"
                          >
                            <Checkbox
                              style={{ marginLeft: '8px' }}
                              checked={
                                Array.isArray(controllerField.value) &&
                                controllerField.value.some((item: any) =>
                                  typeof item === 'object'
                                    ? item.value === option.value
                                    : item === option.value
                                )
                              }
                              onChange={(e) => {
                                const currentValues =
                                  controllerField.value || []
                                if (e.target.checked) {
                                  controllerField.onChange([
                                    ...currentValues,
                                    {
                                      label: option.label,
                                      value: option.value
                                    }
                                  ])
                                } else {
                                  controllerField.onChange(
                                    currentValues.filter((item: any) =>
                                      typeof item === 'object'
                                        ? item.value !== option.value
                                        : item !== option.value
                                    )
                                  )
                                }
                              }}
                              sx={{ paddingLeft: '0px' }}
                            />
                          </Typography>
                          <Typography
                            color={theme.palette.neutral[200]}
                            variant={
                              Array.isArray(controllerField.value) &&
                              controllerField.value.some((item: any) =>
                                typeof item === 'object'
                                  ? item.value === option.value
                                  : item === option.value
                              )
                                ? 'smBold'
                                : 'smLight'
                            }
                          >
                            {option.label}
                          </Typography>
                        </Stack>
                      </Stack>
                    ))}
                    {fieldState.error && (
                      <Box mt="-8px">
                        <Typography ml="4px" color="#CC2942" variant="xsBold">
                          {fieldState.error.message}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )
              }
            />
          </Box>
        )
      }

      case 'radio': {
        // Obter opções da nova estrutura field_configuration.options ou fallback para field.options
        // Priorizar field.options (que vem do useCustomFields mapeado) sobre field.custom_field
        const radioOptions =
          field.options ||
          field.custom_field?.field_configuration?.options ||
          field.field_configuration?.options ||
          []

        const parseRubyHashValue = (value: any) => {
          if (typeof value === 'string' && value.includes('=>')) {
            try {
              const jsonString = value
                .replace(/=>/g, ':')
                .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":')
              return JSON.parse(jsonString)
            } catch (e) {
              console.warn('Erro ao parsear valor Ruby hash:', value)
              return null
            }
          }
          return value
        }

        // Obter valor atual parseado
        const getCurrentValue = () => {
          const rawValue = field.value || field.field_value
          const parsedValue = parseRubyHashValue(rawValue)

          if (typeof parsedValue === 'object' && parsedValue?.value) {
            return parsedValue.value
          }
          return parsedValue || ''
        }

        return (
          <Box>
            <Stack>
              <Typography
                variant="smLight"
                color={theme.palette.neutral[200]}
                sx={{ mb: 0.2 }}
              >
                {field.name}
                {field.required ? '*' : ''}
              </Typography>
              {field.description ? (
                <Typography
                  variant="smLight"
                  sx={{ mb: 0.5 }}
                  color={theme.palette.neutral[400]}
                >
                  {field.description}
                </Typography>
              ) : null}
            </Stack>
            <Controller
              control={control}
              name={fieldName}
              render={({ field: controllerField, fieldState }) => (
                <Select
                  inputRef={controllerField.ref}
                  helperText={fieldState.error?.message}
                  error={!!fieldState.error?.message}
                  fullWidth
                  onChange={(value) => {
                    // Encontrar a opção selecionada para salvar como objeto {label, value}
                    const selectedOption = radioOptions.find(
                      (opt: any) => opt.value === value
                    )
                    if (selectedOption) {
                      controllerField.onChange({
                        label: selectedOption.label,
                        value: selectedOption.value
                      })
                    } else {
                      controllerField.onChange(value)
                    }
                  }}
                  value={
                    typeof controllerField.value === 'object'
                      ? controllerField.value?.value || ''
                      : controllerField.value || getCurrentValue()
                  }
                  displayEmpty
                >
                  <MenuItem value="">
                    <Typography color={theme.palette.neutral[400]}>
                      Selecione uma opção
                    </Typography>
                  </MenuItem>
                  {radioOptions.map((option: any) => (
                    <MenuItem
                      key={option.uuid || option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>
        )
      }

      case 'accept':
        return (
          <Box>
            <Controller
              control={control}
              name={fieldName}
              render={({ field: controllerField, fieldState }) => (
                <Box>
                  {field.custom_field?.field_configuration.type === 'link' && (
                    <Stack>
                      <Typography>
                        {field.custom_field.field_configuration.title}
                      </Typography>
                      <Stack mb="-8px" direction="row" alignItems="center">
                        <Checkbox
                          checked={controllerField.value || false}
                          onChange={controllerField.onChange}
                          sx={{ marginTop: '-6px', marginLeft: '-10px' }}
                        />
                        <Link
                          target="_blank"
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: 'black',
                            fontWeight: '500',
                            marginBottom: 8
                          }}
                          href={field.custom_field?.field_configuration.value}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[200]}
                          >
                            {field.custom_field?.field_configuration
                              ?.accept_text || 'Aceito os termos e condições'}
                          </Typography>
                        </Link>
                      </Stack>
                    </Stack>
                  )}
                  {field.custom_field?.field_configuration.type === 'text' && (
                    <Stack mb="-8px" direction="column" alignItems="flex-start">
                      <Textarea
                        disabled
                        label={
                          field.custom_field.field_configuration.title ||
                          'Termos e condições'
                        }
                        style={{
                          borderRadius: '8px',
                          maxHeight: '120px',
                          overflow: 'initial',
                          overflowY: 'auto',
                          outline: '0',
                          color: theme.palette.neutral['200'],
                          backgroundColor: theme.palette.neutral['A200']
                        }}
                        value={field.custom_field.field_configuration.value}
                        placeholder="Digite aqui os termos e condições"
                        fullWidth
                      />
                      <Stack
                        direction={isMobile ? 'column-reverse' : 'row'}
                        mt={isMobile ? 1 : 0}
                        justifyContent="space-between"
                        alignItems={isMobile ? 'flex-start' : 'center'}
                        width="100%"
                      >
                        <Stack direction="row" alignItems="center">
                          <Checkbox
                            checked={controllerField.value || false}
                            onChange={controllerField.onChange}
                            sx={{ marginLeft: '-10px' }}
                          />
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[200]}
                          >
                            {field.custom_field?.field_configuration
                              ?.accept_text || 'Aceito os termos e condições'}
                          </Typography>
                        </Stack>
                        <Typography
                          onClick={() => setIsModalOpen(true)}
                          sx={{
                            color: theme.palette.neutral[400],
                            textDecoration: 'underline',
                            ':hover': { cursor: 'pointer' }
                          }}
                        >
                          Expandir visualização
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                  {field.custom_field?.field_configuration.type === 'file' && (
                    <Stack>
                      <Typography>
                        {field.custom_field.field_configuration.title}
                      </Typography>
                      <Stack mb="-8px" direction="row" alignItems="center">
                        <Checkbox
                          checked={controllerField.value || false}
                          onChange={controllerField.onChange}
                          sx={{ marginTop: '-6px', marginLeft: '-10px' }}
                        />
                        <Box
                          onClick={(e) => {
                            e.preventDefault()
                            if (
                              field.custom_field?.document_file?.doc_file_url
                            ) {
                              const fileUrl =
                                field.custom_field.document_file.doc_file_url
                              const fileName =
                                field.custom_field.document_file
                                  .doc_file_name || ''

                              openDocumentViewer({
                                url: fileUrl,
                                filename: fileName,
                                fallbackToDirectOpen: true
                              })
                            }
                          }}
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: 'black',
                            fontWeight: '500',
                            marginBottom: 8
                          }}
                        >
                          <Typography
                            variant="smLight"
                            color={theme.palette.neutral[200]}
                          >
                            {field.custom_field?.field_configuration
                              ?.accept_text || 'Aceito os termos e condições'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  )}

                  {fieldState.error && (
                    <Typography ml="4px" color="#CC2942" variant="xsBold">
                      {fieldState.error.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Box>
        )

      case 'upload':
        return renderUploadField()

      case 'date': {
        const dateType = normalizeDateType(
          field.custom_field?.field_configuration?.type ||
            field.field_configuration?.type
        )

        const views =
          dateType === 'year_only'
            ? ['year']
            : dateType === 'month_year'
            ? ['year', 'month']
            : ['year', 'month', 'day']

        const inputFormat =
          dateType === 'year_only'
            ? 'yyyy'
            : dateType === 'month_year'
            ? 'MM/yyyy'
            : 'dd/MM/yyyy'

        const placeholder =
          dateType === 'year_only'
            ? 'AAAA'
            : dateType === 'month_year'
            ? 'MM/AAAA'
            : 'DD/MM/AAAA'

        return (
          <Box>
            <Stack>
              <Typography
                variant="smLight"
                color={theme.palette.neutral[200]}
                sx={{ mb: 0.2 }}
              >
                {field.name}
                {field.required ? '*' : ''}
              </Typography>
              {field.description ? (
                <Typography
                  variant="smLight"
                  sx={{ mb: 0.5 }}
                  color={theme.palette.neutral[400]}
                >
                  {field.description}
                </Typography>
              ) : null}
            </Stack>
            <Controller
              control={control}
              name={fieldName}
              render={({ field: controllerField, fieldState }) => (
                <DateFieldInput
                  controllerField={controllerField}
                  fieldState={fieldState}
                  views={views}
                  inputFormat={inputFormat}
                  placeholder={placeholder}
                  dateType={dateType}
                />
              )}
            />
          </Box>
        )
      }

      default:
        return (
          <Box>
            <Stack>
              <Typography
                variant="smLight"
                color={theme.palette.neutral[200]}
                sx={{ mb: 0.5 }}
              >
                {field.name}
                {field.required ? '*' : ''}
              </Typography>
              {descriptionText ? (
                <Typography
                  variant="smLight"
                  color={theme.palette.neutral[400]}
                >
                  {descriptionText}
                </Typography>
              ) : null}
            </Stack>
            <InputController control={control} name={fieldName}>
              <Textfield
                placeholder="Preencha este campo"
                sx={{
                  '& .MuiInputBase-input::placeholder': {
                    color: theme.palette.neutral[400],
                    opacity: 1
                  }
                }}
              />
            </InputController>
          </Box>
        )
    }
  }

  return (
    <>
      {renderFieldByType()}

      {/* Modal para visualização expandida dos termos */}
      <Modal
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        width="60vw"
        maxWidth="60vw"
        height="90vh"
        maxHeight="90vh"
      >
        <ModalHeader onClose={() => setIsModalOpen(false)}>
          <Typography variant="mdBold">
            {field.custom_field?.field_configuration?.title ||
              'Termos e condições'}
          </Typography>
        </ModalHeader>
        <ModalContent>
          <Box
            sx={{
              width: isMobile ? 'calc(100% - 2px)' : 'calc(60vw - 50px)',
              height: isMobile ? 'auto' : 'calc(90vh - 100px)',
              overflowY: 'auto',
              border: '1px solid #E3E6E3',
              borderRadius: '8px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            <Box p={2}>
              <Typography variant="mdLight" m={2} sx={{ color: '#333' }}>
                {field.custom_field?.field_configuration?.value || ''}
              </Typography>
            </Box>
          </Box>
        </ModalContent>
      </Modal>
    </>
  )
}

export default CustomFieldRenderer
